<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Runs</h1>
        <p class="console-page__placeholder">
          Review queued and completed work across the console, then open a run record for details.
        </p>
        <ConsolePollingStatus
          active
          :busy="refreshBusy"
          idle-label="Auto-refresh every 10s"
          busy-label="Refreshing runs..."
          tone="active"
        />
      </div>

      <div class="console-page__actions">
        <button type="button" class="button button--ghost" :disabled="loading" @click="loadData({ showLoading: true })">
          Refresh
        </button>
        <router-link :to="{ name: 'ConsoleJobCreate' }" class="button button--primary">
          Submit Request
        </router-link>
      </div>
    </div>

    <section class="console-panel">
      <div class="console-filters">
        <label class="console-filter console-filter--wide">
          <span class="console-filter__label">Search</span>
          <input
            v-model="searchQuery"
            type="text"
            class="form-input"
            placeholder="Run ID, request, or workspace"
          />
        </label>

        <label class="console-filter">
          <span class="console-filter__label">Status</span>
          <select v-model="statusFilter" class="form-input">
            <option value="">All</option>
            <option value="attention">Needs Attention</option>
            <option value="planning">Planning</option>
            <option value="awaiting_input">Awaiting Input</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="running">Running</option>
            <option value="delivering">Delivering</option>
            <option value="synthesizing">Synthesizing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>

        <div class="console-filter">
          <span id="runs-workspace-filter-label" class="console-filter__label">Workspace</span>
          <RunsWorkspaceFilter
            v-model="workspaceFilter"
            :workspaces="workspaceOptions"
            labelled-by="runs-workspace-filter-label"
          />
        </div>
      </div>

      <div v-if="workspaceWarning && !error" class="alert alert--error">
        {{ workspaceWarning }}
      </div>

      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="filteredJobs.length === 0"
        loading-message="Loading runs..."
        :empty-message="emptyMessage"
      >
        <div class="console-signal-strip job-summary-strip">
          <div class="console-signal console-signal--active">
            <span class="console-signal__label">Matching Runs</span>
            <span class="console-signal__value">{{ filteredJobs.length }}</span>
            <span class="console-signal__note">Current search and filter result set.</span>
          </div>
          <div class="console-signal" :class="attentionJobs > 0 ? 'console-signal--attention' : 'console-signal--pending'">
            <span class="console-signal__label">Needs Attention</span>
            <span class="console-signal__value">{{ attentionJobs }}</span>
            <span class="console-signal__note">Awaiting input, approval, or credentials.</span>
          </div>
          <div class="console-signal" :class="activeJobs > 0 ? 'console-signal--active' : 'console-signal--pending'">
            <span class="console-signal__label">Active</span>
            <span class="console-signal__value">{{ activeJobs }}</span>
            <span class="console-signal__note">Running, delivering, or synthesizing runs.</span>
          </div>
          <div class="console-signal" :class="failedJobs > 0 ? 'console-signal--failed' : 'console-signal--ready'">
            <span class="console-signal__label">Failed</span>
            <span class="console-signal__value">{{ failedJobs }}</span>
            <span class="console-signal__note">Runs requiring follow-up.</span>
          </div>
        </div>

        <div class="console-results">
          <p class="console-results__count">
            Showing {{ pagedJobs.length }} of {{ filteredJobs.length }} matching runs
          </p>
        </div>

        <div class="console-list job-list">
          <button
            v-for="job in pagedJobs"
            :key="job.jobId"
            type="button"
            class="console-card job-card"
            :class="`console-card--${job.tone}`"
            @click="openJob(job.jobId)"
          >
            <div class="console-card__header job-card__header">
              <div>
                <h2 class="job-card__title">{{ summarizeJobTitle(job.description, job.jobId) }}</h2>
                <p class="job-card__id">{{ job.jobId }}</p>
              </div>

              <div class="console-card__meta job-card__meta">
                <span class="console-badge job-badge" :class="`console-badge--${job.tone}`">
                  {{ job.statusLabel }}
                </span>
              </div>
            </div>

            <div class="console-card__footer job-card__footer">
              <span class="job-card__summary">{{ getWorkspaceLabel(job.workspaceId) }}</span>
              <span class="job-card__date">
                <template v-if="job.updatedAt && job.updatedAt !== job.createdAt">
                  Updated {{ formatRelativeTime(job.updatedAt) }}
                </template>
                <template v-else>
                  Created {{ formatRelativeTime(job.createdAt) }}
                </template>
              </span>
            </div>
          </button>
        </div>

        <ConsolePagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @navigate="goToPage"
        />
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import ConsolePagination from '@/components/ConsolePagination.vue'
import RunsWorkspaceFilter from '@/components/RunsWorkspaceFilter.vue'
import { summarizeJobTitle } from '@/composables/useJobPresentation.js'
import { formatRelativeTime } from '@/composables/useRelativeTime.js'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { fetchWorkspaceOptions } from '@/services/workspaceOptions.js'
import { fetchAllJobSummaryItems } from '@/services/jobSummary.js'

const router = useRouter()
const route = useRoute()
const { requestJson } = useOrchestratorRequest()
const logger = useFrontendLogger().child('[ConsoleJobs]')
const REFRESH_INTERVAL_MS = 10000

const loading = ref(true)
const error = ref('')
const workspaceWarning = ref('')
const jobs = ref([])
const workspaces = ref([])
const searchQuery = ref('')
const statusFilter = ref('')
const workspaceFilter = ref('')
const currentPage = ref(1)
const pageSize = ref(12)
const refreshBusy = ref(false)
let refreshInterval = null

const ATTENTION_STATUSES = ['awaiting_input', 'pending_approval', 'waiting_credentials']

const workspaceOptions = computed(() => {
  return [...workspaces.value].sort((a, b) => a.name.localeCompare(b.name))
})

const filteredJobs = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  return jobs.value.filter((job) => {
    if (statusFilter.value === 'attention') {
      if (!ATTENTION_STATUSES.includes(job.status)) return false
    } else if (statusFilter.value && job.status !== statusFilter.value) {
      return false
    }
    if (workspaceFilter.value && job.workspaceId !== workspaceFilter.value) return false

    if (!query) return true

    const haystack = [
      job.jobId,
      job.description ?? '',
      getWorkspaceLabel(job.workspaceId),
    ].join('\n').toLowerCase()

    return haystack.includes(query)
  })
})

const attentionJobs = computed(() => filteredJobs.value.filter((job) => ATTENTION_STATUSES.includes(job.status)).length)
const activeJobs = computed(() => filteredJobs.value.filter((job) => ['running', 'delivering', 'synthesizing'].includes(job.status)).length)
const failedJobs = computed(() => filteredJobs.value.filter((job) => job.status === 'failed').length)

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(filteredJobs.value.length / pageSize.value))
})

const pagedJobs = computed(() => {
  const offset = (currentPage.value - 1) * pageSize.value
  return filteredJobs.value.slice(offset, offset + pageSize.value)
})

const emptyMessage = computed(() => {
  if (searchQuery.value || statusFilter.value || workspaceFilter.value) {
    return 'No runs match the current filters.'
  }
  return 'No runs found.'
})

function normalizeWorkspace(raw) {
  const workspaceId = raw.id ?? raw.workspaceId
  return {
    workspaceId,
    name: raw.name || workspaceId,
  }
}

function summarizeStatusGroup(status) {
  if (status === 'completed') return 'completed'
  if (status === 'failed') return 'failed'
  if ([
    'attention',
    'planning',
    'awaiting_input',
    'pending_approval',
    'approved',
    'running',
    'delivering',
    'synthesizing',
    'waiting_credentials',
  ].includes(status)) {
    return 'active'
  }
  return undefined
}

async function fetchJobs() {
  const group = summarizeStatusGroup(statusFilter.value)
  jobs.value = await fetchAllJobSummaryItems(requestJson, {
    ...(group ? { group } : {}),
    ...(workspaceFilter.value ? { workspaceId: workspaceFilter.value } : {}),
  })
}

async function fetchWorkspaces() {
  const rawWorkspaces = await fetchWorkspaceOptions(requestJson, { includeArchived: true })
  workspaces.value = rawWorkspaces.map(normalizeWorkspace)
}

async function loadData({ showLoading = false, background = false } = {}) {
  if (showLoading) loading.value = true
  if (background) refreshBusy.value = true
  if (!background) {
    error.value = ''
    workspaceWarning.value = ''
  }

  const [jobsResult, workspacesResult] = await Promise.allSettled([
    fetchJobs(),
    fetchWorkspaces(),
  ])

  if (jobsResult.status === 'rejected') {
    if (!background) {
      jobs.value = []
      error.value = 'Failed to load runs.'
    }
    logger.warn('Failed to fetch runs', jobsResult.reason)
  } else {
    error.value = ''
  }

  if (workspacesResult.status === 'rejected') {
    if (!background) {
      workspaces.value = []
      workspaceWarning.value = 'Workspace names could not be loaded. Showing workspace IDs instead.'
    }
    logger.warn('Failed to fetch workspaces for runs page', workspacesResult.reason)
  } else {
    workspaceWarning.value = ''
  }

  if (currentPage.value > totalPages.value) {
    currentPage.value = totalPages.value
  }

  loading.value = false
  refreshBusy.value = false
}

function startRefreshInterval() {
  if (refreshInterval) clearInterval(refreshInterval)
  refreshInterval = setInterval(() => {
    void loadData({ background: true })
  }, REFRESH_INTERVAL_MS)
}

function goToPage(page) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
}

function openJob(jobId) {
  router.push({ name: 'ConsoleJobDetail', params: { id: jobId } })
}

function getWorkspaceLabel(workspaceId) {
  const match = workspaces.value.find((workspace) => workspace.workspaceId === workspaceId)
  return match?.name || workspaceId
}

function normalizeStatusFilter(value) {
  const normalized = typeof value === 'string' ? value.trim() : ''
  const supported = [
    '',
    'attention',
    'planning',
    'awaiting_input',
    'pending_approval',
    'approved',
    'running',
    'delivering',
    'synthesizing',
    'completed',
    'failed',
    'cancelled',
  ]

  return supported.includes(normalized) ? normalized : ''
}

function normalizeWorkspaceFilter(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function syncRouteQuery() {
  const nextQuery = {
    ...route.query,
  }

  if (statusFilter.value) {
    nextQuery.status = statusFilter.value
  } else {
    delete nextQuery.status
  }

  if (workspaceFilter.value) {
    nextQuery.workspaceId = workspaceFilter.value
  } else {
    delete nextQuery.workspaceId
  }

  if (route.query.status !== nextQuery.status || route.query.workspaceId !== nextQuery.workspaceId) {
    router.replace({ query: nextQuery })
  }
}

watch(
  () => route.query.status,
  (status) => {
    statusFilter.value = normalizeStatusFilter(status)
  },
  { immediate: true },
)

watch(
  () => route.query.workspaceId,
  (workspaceId) => {
    workspaceFilter.value = normalizeWorkspaceFilter(workspaceId)
  },
  { immediate: true },
)

watch(searchQuery, () => {
  currentPage.value = 1
})

watch([statusFilter, workspaceFilter], ([status, workspace], [previousStatus, previousWorkspace]) => {
  currentPage.value = 1
  if (status === previousStatus && workspace === previousWorkspace) return
  syncRouteQuery()
  loadData({ showLoading: true })
})

onMounted(() => {
  loadData({ showLoading: true })
  startRefreshInterval()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
})
</script>

<style scoped>
.console-filters {
  grid-template-columns: minmax(0, 1.8fr) repeat(2, minmax(180px, 0.6fr));
}

.console-filter--wide {
  min-width: 0;
}

.job-summary-strip {
  margin-bottom: 16px;
}

.job-card__title {
  margin: 0;
  font-family: var(--shell-console-font);
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--shell-text);
}

.job-card__id,
.job-card__summary,
.job-card__date {
  color: var(--shell-text-muted);
}

.job-card__id,
.job-card__summary,
.job-card__date {
  font-size: 0.84rem;
}

.job-card__id {
  margin: 10px 0 0;
  font-family: "Courier New", Courier, monospace;
}

.job-card__footer {
  margin-top: 16px;
  align-items: center;
}

@media (max-width: 768px) {
  .console-filters {
    grid-template-columns: 1fr;
  }
}
</style>
